'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { user } = await signIn(email, password);
      // Redirect based on user role
      const isAdmin =
        user?.user_metadata?.role === 'super_admin' ||
        user?.app_metadata?.role === 'super_admin';
      router.push(isAdmin ? '/super-admin-dashboard' : '/guest-dashboard');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: '#050D1A' }}
    >
      {/* Subtle texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #C9A96E 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative w-full max-w-md">
        {/* Logo area */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-5">
            <AppLogo size={56} variant="light" />
          </div>
          <div>
            <h1
              className="text-white mb-1"
              style={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: '1.5rem',
                fontWeight: 400,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
              }}
            >
              JAVA APARTMENTS
            </h1>
            <p
              className="text-[#C9A96E]"
              style={{ fontSize: '0.6rem', letterSpacing: '0.25em', textTransform: 'uppercase', fontWeight: 600 }}
            >
              Administration Portal
            </p>
          </div>
        </div>

        {/* Card */}
        <div
          className="bg-white overflow-hidden"
          style={{ borderRadius: '2px', boxShadow: '0 32px 80px rgba(5,13,26,0.5)' }}
        >
          {/* Gold top accent */}
          <div className="h-0.5" style={{ background: 'linear-gradient(90deg, #C9A96E, #E8D5A3, #C9A96E)' }} />

          {/* Form */}
          <div className="px-8 py-8">
            <h2
              className="text-[#0B1628] mb-1"
              style={{ fontSize: '1.1rem', fontWeight: 600, letterSpacing: '0.04em' }}
            >
              Welcome Back
            </h2>
            <p className="text-[#6B6560] text-sm mb-7">Sign in to your admin account</p>

            {error && (
              <div
                className="mb-5 flex items-start gap-3 px-4 py-3"
                style={{ background: '#FDF2F3', border: '1px solid #F5C6CB', borderRadius: '2px' }}
              >
                <AlertCircle size={15} className="text-[#9B2335] mt-0.5 shrink-0" />
                <p className="text-sm text-[#9B2335]">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label
                  className="block text-xs font-semibold text-[#0B1628] mb-2 tracking-widest uppercase"
                  style={{ letterSpacing: '0.12em' }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-3 text-sm text-[#0B1628] placeholder-[#6B6560] bg-[#F9F7F4] border border-[#E2DDD6] focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-all"
                  style={{ borderRadius: '2px' }}
                />
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label
                    className="block text-xs font-semibold text-[#0B1628] tracking-widest uppercase"
                    style={{ letterSpacing: '0.12em' }}
                  >
                    Password
                  </label>
                  <Link
                    href="/forgot-password"
                    className="text-xs text-[#C9A96E] hover:text-[#B8924A] font-medium transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="w-full px-4 py-3 pr-11 text-sm text-[#0B1628] placeholder-[#6B6560] bg-[#F9F7F4] border border-[#E2DDD6] focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-all"
                    style={{ borderRadius: '2px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6560] hover:text-[#0B1628] transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 font-semibold py-3.5 text-sm tracking-widest uppercase transition-all duration-200 mt-2 btn-scale disabled:opacity-60"
                style={{ background: '#0B1628', color: 'white', borderRadius: '2px', letterSpacing: '0.12em' }}
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn size={15} />
                    Sign In
                  </>
                )}
              </button>
            </form>

            {/* Back to site */}
            <div className="mt-6 text-center">
              <Link
                href="/"
                className="text-xs text-[#6B6560] hover:text-[#C9A96E] transition-colors tracking-widest uppercase"
                style={{ letterSpacing: '0.1em' }}
              >
                ← Back to JAVA Apartments
              </Link>
            </div>
          </div>
        </div>

        {/* Demo credentials */}
        <div
          className="mt-4 px-5 py-4"
          style={{ background: 'rgba(201,169,110,0.08)', border: '1px solid rgba(201,169,110,0.2)', borderRadius: '2px' }}
        >
          <p className="text-xs text-[#C9A96E] font-semibold mb-1 tracking-widest uppercase" style={{ letterSpacing: '0.12em' }}>Admin Credentials</p>
          <p className="text-xs text-white/60">Email: javaapartmentstz@gmail.com</p>
          <p className="text-xs text-white/60">Password: JavaAdmin2024!</p>
        </div>
      </div>
    </div>
  );
}
